import { Box, Divider, Typography, useTheme } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import AgeRestriction from 'assets/icons/age_restriction.svg';
import CapacityIcon from 'assets/icons/capacity.svg';
import DarkLogo from 'assets/icons/dark-logo.svg';
import SendIcon from 'assets/icons/request_send_icon.svg';
import TimeIcon from 'assets/icons/time.svg';
import ReferredByImage from 'assets/images/referred_by_img.png';
import customerRequestServices from 'lib/features/customerRequest/customerRequestService';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import { useParams } from 'react-router-dom';
import { parseImageUrl } from 'util/parseImageUrl';

const settings = {
  showThumbs: false,
  showArrows: false,
  showStatus: false,
  emulateTouch: true,
  autoPlay: true,
  interval: 3000,
  infiniteLoop: true,
  stopOnHover: true,
  dynamicHeight: true,
};

const RequestFollowUp = () => {
  const theme = useTheme();

  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [customerRequestDetails, setCustomerRequestDetails] =
    useState<any>(null);

  useEffect(() => {
    if (id) {
      customerRequestServices
        .getCustomerRequest(Number(id))
        .then((response) => {
          setCustomerRequestDetails(response.data);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching customer request:', error);
          setLoading(false);
        });
    }
  }, [id]);

  if (loading) {
    return (
      <Box
        position="fixed"
        top="0"
        zIndex="999"
        left="0"
        width="100%"
        height="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!customerRequestDetails) {
    return (
      <Box
        component="main"
        position="relative"
        maxWidth="680px"
        minHeight="100vh"
        mx="auto"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography fontFamily={theme.typography.fontFamily}>
          Error: Customer request not found.
        </Typography>
        ;
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        padding: '20px 0px',

        position: 'relative',
      }}
    >
      <Box
        component="main"
        position="relative"
        sx={{ backgroundColor: '#fff' }}
        maxWidth="680px"
        mx="auto"
      >
        {/* Top Container */}
        <Box sx={{ padding: '0 15px', marginBottom: '20px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <img src={SendIcon} alt="send-icon" />

            <Typography
              fontSize={22}
              sx={{
                display: 'inline',
                fontWeight: '500',
                color: '#000',
                fontFamily: theme.typography.fontFamily,
              }}
            >
              Your booking request has been sent to{' '}
              <Typography
                fontSize={22}
                sx={{
                  display: 'inline',
                  textDecoration: 'underline',
                  fontWeight: '500',
                  color: '#000',
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                {customerRequestDetails?.referral?.item?.business?.business}
              </Typography>
              !
            </Typography>
          </Box>
          <Typography
            fontSize={17}
            sx={{
              marginTop: '20px',

              fontWeight: '400',
              color: '#000',
              lineHeight: '25px',
              fontFamily: theme.typography.fontFamily,
            }}
          >
            Check your texts! If the time is available, you will be sent a new
            booking link to reserve with payment.
          </Typography>
        </Box>

        {/* Carousel Container */}
        <Carousel {...settings}>
          {/* {referral?.item?.itemImages?.map((image: Image, index: number) => ( */}
          {customerRequestDetails?.referral?.item?.itemImages.map(
            (image: any, index: number) => (
              <div
                key={index}
                style={{
                  width: '100%',
                  height: '300px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <img
                  src={parseImageUrl(image?.uri)}
                  alt={`Item ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </div>
            )
          )}
        </Carousel>

        {/* Bottom Container */}
        <Box
          sx={{
            padding: '0 15px',
            marginTop: '20px',
          }}
        >
          {/* Date Time Container */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingBottom: '5px',
              borderBottom: '1px solid #8D8D8D',
            }}
          >
            <Box sx={{ textAlign: 'left', width: '48%' }}>
              <Typography
                fontWeight="600"
                sx={{
                  fontSize: '17px',
                  color: '#000',
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                Date
              </Typography>
              <Typography
                fontWeight="400"
                sx={{
                  fontSize: '15px',
                  color: '#000',
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                {moment(customerRequestDetails?.requested_date).format(
                  'MM/DD/YY'
                )}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '25px',
                width: '52%',
              }}
            >
              {/* Vertical Divider */}

              <Divider
                orientation="vertical"
                flexItem
                sx={{ borderColor: '#8D8D8D' }}
              />

              <Box sx={{ textAlign: 'left' }}>
                <Typography
                  fontWeight="600"
                  sx={{
                    fontSize: '17px',
                    color: '#000',
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  Time
                </Typography>
                <Typography
                  fontWeight="400"
                  sx={{
                    fontSize: '15px',
                    color: '#000',
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {moment(customerRequestDetails?.requested_time, 'HH:mm:ssZ')
                    .utcOffset('+05:00')
                    .format('hh:mm A')}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Typography
            fontSize={17}
            align="center"
            textAlign={'left'}
            fontWeight={400}
            color="#0D0D0D"
            sx={{
              marginTop: '12px',
              fontFamily: theme.typography.fontFamily,
            }}
          >
            {customerRequestDetails?.referral?.item?.itemDetails[0]?.item_name}
          </Typography>

          <Typography
            fontSize={13}
            align="center"
            textAlign={'left'}
            fontWeight={400}
            color="#0D0D0D"
            sx={{
              marginTop: '3px',
              fontFamily: theme.typography.fontFamily,
            }}
          >
            {customerRequestDetails?.referral?.item?.itemDetails[0]?.headline}
          </Typography>

          {/* Stats Container */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 4,
              marginTop: '10px',
              marginBottom: 1,
            }}
          >
            {/* Capacity */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <img src={CapacityIcon} alt="capacity-icon" />
              <Typography
                fontSize={17}
                fontWeight={400}
                sx={{
                  color: '#0D0D0D',
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                {
                  customerRequestDetails?.referral?.item?.itemDetails[0]
                    ?.capacity
                }{' '}
                max
              </Typography>
            </Box>

            {/* Duration */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <img src={TimeIcon} alt="time-icon" />
              <Typography
                fontSize={17}
                fontWeight={400}
                sx={{
                  color: '#0D0D0D',
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                3hr
              </Typography>
            </Box>

            {/* Age Restriction */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <img src={AgeRestriction} alt="age-icon" />
              <Typography
                fontSize={17}
                fontWeight={400}
                sx={{
                  color: '#0D0D0D',
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                {customerRequestDetails?.referral?.item?.itemDetails[0]
                  ?.min_age ?? 12}
                +
              </Typography>
            </Box>
          </Box>

          <Typography
            fontSize={17}
            align="center"
            textAlign={'left'}
            fontWeight={400}
            color="#0D0D0D"
            sx={{
              marginTop: '12px',
              fontFamily: theme.typography.fontFamily,
            }}
          >
            Group Type
          </Typography>

          <Typography
            fontSize={15}
            align="center"
            textAlign={'left'}
            fontWeight={400}
            color="#0D0D0D"
            sx={{
              marginTop: '3px',
              fontFamily: theme.typography.fontFamily,
            }}
          >
            Bachelorette Party
          </Typography>

          {/* Price Container */}
          <Box
            sx={{
              marginTop: '20px',
            }}
          >
            <Box
              sx={{
                padding: '13px 16px',
                borderRadius: '8px',
                border: '1px solid #8D8D8D',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '15px',
              }}
            >
              {/* Left */}
              <Box>
                <Typography
                  fontSize={20}
                  fontWeight={500}
                  color="#0D0D0D"
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  Starting Price
                </Typography>

                <Typography
                  fontSize={11}
                  fontWeight={400}
                  color="#0D0D0D"
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  Price per group, up to 10 people
                </Typography>
              </Box>
              {/* Right */}
              <Box>
                <Typography
                  fontSize={22}
                  fontWeight={600}
                  color="#0D0D0D"
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  $ {customerRequestDetails?.price}
                </Typography>

                <Typography
                  fontSize={11}
                  fontWeight={400}
                  color="#0D0D0D"
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  + ${(customerRequestDetails?.price * 0.05).toFixed(0)} (Tax &
                  Fees)
                </Typography>
              </Box>
            </Box>
            <Typography
              fontSize={13}
              fontStyle={'italic'}
              textAlign={'left'}
              fontWeight={400}
              color="#0D0D0D"
              sx={{
                marginTop: '3px',
                fontFamily: theme.typography.fontFamily,
              }}
            >
              *Subject to change if modifications are required
            </Typography>
          </Box>

          {/* Referral Container */}
          <Box
            sx={{
              marginTop: '20px',
              marginBottom: '70px',
            }}
          >
            <Typography
              fontWeight={600}
              fontSize={22}
              mb={1}
              fontFamily={theme.typography.fontFamily}
            >
              Referred by
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'end',
                gap: 2,
              }}
            >
              <img
                src={ReferredByImage}
                alt={`user_avatar`}
                style={{
                  width: '40px',
                  height: '40px',
                  objectFit: 'contain',
                  borderRadius: 20,
                }}
              />

              <Typography
                fontWeight={400}
                fontSize={17}
                mb={1}
                fontFamily={theme.typography.fontFamily}
              >
                Sadie B.
              </Typography>
            </Box>

            <Typography
              fontSize={13}
              fontStyle={'italic'}
              textAlign={'left'}
              fontWeight={400}
              color="#0D0D0D"
              sx={{
                marginTop: '3px',
                fontFamily: theme.typography.fontFamily,
              }}
            >
              “Tiki tours is one of the best companies in the area! They know I
              referred you and will be sure to go the extra mile!”
            </Typography>
          </Box>

          {/* Powered by Container */}
          <Typography textAlign="center" fontSize={'15px'} color={'#8D8D8D'}>
            Powered by
          </Typography>
          <Box display="flex" justifyContent="center">
            <img
              src={DarkLogo}
              alt="logo"
              style={{ marginLeft: 'auto', marginRight: 'auto' }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RequestFollowUp;
